import { useCallback, useState } from "react";
import { DocumentData } from "../../components/documents/DocumentCollector/types";
import { useUpdateDocumentDetailsMutation } from "../../graphql/supergraphHooks";
import { type DocumentValueInput } from "../../graphql/supergraphTypes";
import { clientContext } from "../../utils/graphql_utils";

/**
 * A hook that provides functionality for updating document metadata
 *
 * @param caseId - The ID of the case the documents belong to
 * @param profileId - The ID of the profile the documents belong to
 * @returns Object containing function to update document metadata and loading state
 */
export default function useDocumentMetadata(
  caseId: number | undefined,
  profileId: number | undefined,
) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateDocumentDetails, { loading: updateLoading }] = useUpdateDocumentDetailsMutation({
    context: clientContext(),
    refetchQueries: ["GetDocumentDetails"],
    awaitRefetchQueries: true,
  });

  /**
   * Updates metadata for multiple documents in parallel
   * - Sets document purpose (baseType)
   * - Associates with profile
   * - Adds metadata from subType
   *
   * @param documents - Documents with metadata to update (only those with IDs are processed)
   */
  const updateDocumentMetadata = useCallback(
    async (documents: DocumentData[]) => {
      if (documents.length === 0 || !caseId) {
        return;
      }

      setIsUpdating(true);
      try {
        const updates = documents
          .filter((data) => !!data.id)
          .map(async (data) => {
            const metadata: DocumentValueInput[] = data.subType
              ? [
                  {
                    key: "type",
                    value: data.subType || "",
                  },
                ]
              : [];
            await updateDocumentDetails({
              variables: {
                caseId,
                documentId: data.id!,
                updateInfo: {
                  profileId,
                  documentPurpose: data.baseType,
                  metadata: metadata,
                },
              },
            });
          });

        await Promise.all(updates);
      } finally {
        setIsUpdating(false);
      }
    },
    [caseId, profileId, updateDocumentDetails],
  );

  return {
    updateDocumentMetadata,
    loading: isUpdating || updateLoading,
  };
}
